import React, { useState, useEffect, useRef } from "react";
import OptimizedImg from "../../components/OptimizedImg";
import { CONTACT_PHONE_TEL } from "../../config/contactConfig";
import { cssImageSetValue } from "../../utils/media";

/* ─── Request-a-Demo mailto link (matches CardFlow / Samone / HEM pattern) ───
   Built once and reused by both "Request a Demo" CTAs so the subject/body
   stay in sync if either is ever edited. */
const DEMO_EMAIL = "hr@sagveektechnologies.in";
const DEMO_SUBJECT = "Demo Request - Electrical Panel Management";
const DEMO_BODY = [
  "Hi Sagveek Technologies team,",
  "",
  "I would like to request a demo of the Electrical Panel Management platform.",
  "",
  "Please let me know a suitable time to connect.",
  "",
  "Thanks,",
].join("\n");

const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
  DEMO_SUBJECT
)}&body=${encodeURIComponent(DEMO_BODY)}`;

const DATA = {
  name: "Electrical Panel Management",
  ticketNo: "EPM-0001",
  category: "Industrial · MEP Management",
  tagline: "Every panel, wired for visibility.",
  description:
    "A field-to-office platform for tracking electrical control panels across their whole life — manufacturing, supply, site commissioning and warranty — with a QR code on every panel linking straight back to its record.",
  heroImage: "/images/project4/1.png",
  dashboardImage: "/images/project4/2.png",
  screenshots: [
    { src: "/images/project4/3.png", label: "User Management" },
    { src: "/images/project4/4.png", label: "Login" },
    { src: "/images/project4/5.png", label: "Panel Report & History" },
  ],
  stats: [
    { value: 600, suffix: "+", label: "Panels Tracked" },
    { value: 64, suffix: "", label: "Site Engineers" },
    { value: 40, suffix: "%", label: "Faster Commissioning" },
    { value: 24, suffix: "/7", label: "Support Desk" },
  ],
  benefits: [
    { icon: "⌁", title: "Cut the Paper Trail", desc: "Scan a panel's QR code on-site and its full record is right there — no logbooks, no lost sheets." },
    { icon: "▦", title: "Full Fleet Visibility", desc: "See every panel's status across every site, from first manufacture to final commissioning." },
    { icon: "◈", title: "Centralize Compliance", desc: "Protection settings, warranty dates and commissioning history, stored against the panel for good." },
    { icon: "⏱", title: "Track Commissioning Live", desc: "Know today's supplied and commissioned counts without chasing a single site engineer." },
  ],
  features: [
    { icon: "🗂️", title: "Panel & Asset Registration", desc: "Log every panel with catalogue, serial number, type and pump configuration." },
    { icon: "📱", title: "QR Code Tracking", desc: "Each panel gets a scannable QR code linking directly to its live record." },
    { icon: "👥", title: "User & Role Management", desc: "Separate access for factory users, dealers, site and commissioning engineers." },
    { icon: "⚡", title: "Protection Setting Records", desc: "Capture voltage and current protection settings at commissioning time." },
    { icon: "🛡️", title: "Warranty & Service Tracking", desc: "Warranty start and end dates calculated and tracked automatically." },
    { icon: "📊", title: "Real-time Reports & Exports", desc: "Filter by site, date or panel type and export straight to CSV or Excel." },
  ],
  steps: [
    { n: "01", title: "Register Panel", desc: "Add the panel with its catalogue, serial number and manufacturing date." },
    { n: "02", title: "Assign to Site", desc: "Link the panel to a customer, dealer and site address." },
    { n: "03", title: "Supply & Install", desc: "Record supply date as the panel leaves the factory floor." },
    { n: "04", title: "Commission On-site", desc: "Site engineers log commissioning date and protection settings." },
    { n: "05", title: "Track & Report", desc: "Pull warranty status and history any time by scanning the QR code." },
  ],
  detailedFeatures: [
    "User Roles & Permissions", "Multi-site Management", "QR Code Scanning",
    "Voltage Protection Settings", "Warranty Tracking", "Commissioning History",
    "CSV & Excel Export", "Dealer & Engineer Access", "Panel Document Uploads",
  ],
  capabilities: [
    { k: "Deployment", v: "Cloud-based (SaaS)" },
    { k: "Access", v: "Web & QR / Mobile" },
    { k: "Integrations", v: "CSV, Excel, Print" },
    { k: "Security", v: "Role-based & Encrypted" },
    { k: "Support", v: "24/7 Phone & Email" },
    { k: "Scale", v: "Single Site → Multi-site Network" },
  ],
  testimonials: [
    { text: "We can trace any panel back to its commissioning sheet in seconds now.", name: "Rakesh Deshmukh", role: "Site Engineer", tag: "Khed, Pune" },
    { text: "Warranty disputes used to take days to resolve. Now it's one QR scan.", name: "Anjali Kulkarni", role: "Dealer Network", tag: "12 sites" },
    { text: "Our commissioning engineers stopped carrying paper checklists entirely.", name: "Suresh Patil", role: "Commissioning Lead", tag: "AG Corporation" },
  ],
  faq: [
    { q: "What is the Electrical Panel Management platform?", a: "A platform that tracks control panels from manufacturing through supply, commissioning and warranty, with a QR code on every panel linking to its live record." },
    { q: "How does the QR code work?", a: "Each panel is issued a unique QR code at registration. Scanning it in the field opens that panel's full record — no login needed to view basic details." },
    { q: "Can dealers and site engineers use it too?", a: "Yes — factory users, dealers, site engineers and commissioning engineers each get access scoped to what they need." },
    { q: "Is my data secure?", a: "Yes, access is role-based and encrypted, with activity tracked against individual user accounts." },
  ],
};

/* ─── Tokens (#6C0A9F Theme Palette) ─── */
const C = {
  bg: "#FCFAFF",
  surface: "#FFFFFF",
  raised: "#F7F2FA",
  border: "#E9DDF5",
  muted: "#D6C4EB",
  dimText: "#7E6E92",
  subText: "#524364",
  text: "#1E122A",
  bright: "#150222",
  navy: "#6C0A9F",
  navyD: "#140220",
  amber: "#6C0A9F",
  amberD: "#52067A",
  accentLight: "#D8B4FE",
  cardHoverGlow: "rgba(108, 10, 159, 0.16)",
};

/* ─── Typography (Consistent with Outfit & Inter) ─── */
const FF = {
  display: "'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

/* ─── Hooks ─── */
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function useCountUp(target, active, duration = 2200, delay = 0) {
  const [n, setN] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!active || done.current) return;
    done.current = true;
    let raf;
    const begin = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setN(Math.round(ease * target));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(begin, delay);
    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, target, duration, delay]);
  return n;
}

/* ─── Sub-components ─── */
function StatTile({ value, suffix, label, active, delay = 0 }) {
  const n = useCountUp(value, active, 2200, delay);
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        textAlign: "center",
        padding: "36px 20px",
        background: C.surface,
        border: `1px solid ${hov ? C.navy : C.border}`,
        borderRadius: 14,
        boxShadow: hov ? `0 20px 44px ${C.cardHoverGlow}` : "0 4px 16px rgba(108,10,159,0.04)",
        transform: hov ? "translateY(-5px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      <div style={{ fontFamily: FF.display, fontSize: "clamp(34px, 5vw, 48px)", fontWeight: 700, color: C.bright, lineHeight: 1, letterSpacing: "-1px" }}>
        {n}
        <span style={{ color: C.navy }}>{suffix}</span>
      </div>
      <div style={{ fontFamily: FF.body, fontSize: 12.5, color: C.dimText, marginTop: 10, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.navy : C.border}`,
        borderRadius: 16,
        padding: "26px 24px",
        transition: "all 0.25s ease",
        cursor: "default",
        transform: hov ? "translateY(-4px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.cardHoverGlow}` : "0 4px 14px rgba(108,10,159,0.04)",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: hov ? C.navy : C.raised,
          color: hov ? "#fff" : C.navy,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          marginBottom: 16,
          transition: "all 0.25s ease",
          boxShadow: hov ? "0 6px 16px rgba(108,10,159,0.3)" : "none",
        }}
      >
        {icon}
      </div>
      <h3 style={{ fontFamily: FF.display, fontSize: 18, fontWeight: 700, color: hov ? C.navy : C.bright, margin: "0 0 8px", letterSpacing: "-0.2px", transition: "color 0.2s" }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{desc}</p>
    </div>
  );
}

function FeatureTile({ title }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.navy : C.border}`,
        borderRadius: 12,
        padding: "16px 18px",
        transition: "all 0.2s ease",
        cursor: "default",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 14px 32px ${C.cardHoverGlow}` : "0 2px 8px rgba(108,10,159,0.04)",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: hov ? C.navy : "#F3E8FF",
          color: hov ? "#fff" : C.navy,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          transition: "all 0.2s ease",
        }}
      >
        ✓
      </div>
      <span style={{ fontFamily: FF.body, fontSize: 14, fontWeight: 600, color: hov ? C.navy : C.text, transition: "color 0.2s ease" }}>{title}</span>
    </div>
  );
}

function Section({ children, style }) {
  return (
    <section className="epm-section" style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 24px", ...style }}>
      {children}
    </section>
  );
}

function Label({ text }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontFamily: FF.body,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "1px",
        textTransform: "uppercase",
        color: C.navy,
        background: "#F4E8FC",
        padding: "5px 16px",
        borderRadius: 30,
        marginBottom: 16,
      }}
    >
      {text}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2
      style={{
        fontFamily: FF.display,
        fontSize: "clamp(28px, 3.6vw, 42px)",
        fontWeight: 800,
        color: C.bright,
        margin: "0 0 16px",
        letterSpacing: "-0.8px",
        lineHeight: 1.18,
      }}
    >
      {children}
    </h2>
  );
}

function CircuitDivider() {
  return (
    <div style={{ height: 46, overflow: "hidden", position: "relative" }}>
      <svg
        viewBox="0 0 1200 46"
        preserveAspectRatio="none"
        style={{ width: "200%", height: "100%", animation: "epm-scroll 9s linear infinite" }}
      >
        <polyline
          fill="none"
          stroke={C.navy}
          strokeWidth="2"
          strokeLinecap="square"
          strokeLinejoin="miter"
          points="0,23 90,23 90,10 160,10 160,36 230,36 230,23 340,23 340,23 430,23 430,10 500,10 500,36 570,36 570,23 680,23 680,23 770,23 770,10 840,10 840,36 910,36 910,23 1020,23 1020,23 1110,23 1110,10 1180,10 1180,23 1200,23"
        />
        {[90, 160, 230, 430, 500, 570, 770, 840, 910, 1110, 1180].map((cx, i) => (
          <circle key={i} cx={cx} cy={i % 2 === 0 ? 23 : cx % 3 === 0 ? 10 : 36} r="2.5" fill={C.accentLight} />
        ))}
      </svg>
    </div>
  );
}

function ScreenshotCard({ src, label }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.surface,
        border: `1.5px solid ${hov ? C.navy : C.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: hov ? `0 20px 44px ${C.cardHoverGlow}` : "0 4px 14px rgba(108,10,159,0.06)",
        transform: hov ? "translateY(-5px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      <div style={{ background: C.raised, height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <OptimizedImg src={src} alt={label} loading="lazy" pictureStyle={{ width: "100%", height: "100%", display: "block" }} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </div>
      <div style={{ padding: "16px 20px", borderTop: `1px solid ${C.border}`, background: C.surface }}>
        <span style={{ fontFamily: FF.display, fontSize: 14.5, fontWeight: 700, color: hov ? C.navy : C.bright, transition: "color 0.2s" }}>{label}</span>
      </div>
    </div>
  );
}

/* ─── Page Component ─── */
const AGCorporationProductPage = () => {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        .epm-root, .epm-root *{ box-sizing:border-box; }
        @keyframes epm-scroll{ from{ transform:translateX(0); } to{ transform:translateX(-50%); } }
        @media(prefers-reduced-motion:reduce){ .epm-root svg[style]{ animation:none!important; } }

        /* ─── Responsive grids ─── */
        .epm-grid-2, .epm-grid-2-start { display:grid; grid-template-columns: 1fr 1fr; gap: 60px 80px; }
        .epm-grid-2 { align-items: center; }
        .epm-grid-2-start { align-items: start; }
        .epm-grid-3 { display:grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .epm-grid-4 { display:grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }

        @media (max-width: 900px) {
          .epm-grid-2, .epm-grid-2-start { grid-template-columns: 1fr; gap: 40px; }
          .epm-grid-4 { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 700px) {
          .epm-grid-3 { grid-template-columns: 1fr; }
        }
        @media (max-width: 520px) {
          .epm-grid-4 { grid-template-columns: 1fr; }
        }

        /* ─── Responsive section padding ─── */
        @media (max-width: 768px) {
          .epm-section { padding: 60px 20px !important; }
        }
        @media (max-width: 480px) {
          .epm-section { padding: 44px 16px !important; }
        }

        /* ─── Misc mobile fixes ─── */
        @media (max-width: 480px) {
          .epm-faq-row { flex-wrap: wrap; }
          .epm-cta-row { flex-direction: column; align-items: stretch; }
          .epm-cta-row a { text-align: center; }
        }
      `}</style>

      <div className="epm-root" style={{ background: C.bg, color: C.text, fontFamily: FF.body, minHeight: "100vh", overflowX: "hidden" }}>
        {/* ══ HERO ══ */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            backgroundImage: cssImageSetValue(DATA.heroImage),
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(100deg, ${C.navyD}F2 0%, ${C.navyD}E0 36%, rgba(108,10,159,0.78) 75%, ${C.navyD}55 100%)`,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "-10%",
              right: "-6%",
              width: 520,
              height: 520,
              background: `radial-gradient(circle, ${C.navy}44 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          <Section style={{ paddingTop: 40, paddingBottom: 36, position: "relative", zIndex: 1 }}>
            <div style={{ maxWidth: 650 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  border: `1px solid rgba(216,180,254,0.3)`,
                  borderRadius: 30,
                  padding: "6px 16px 6px 8px",
                  marginBottom: 28,
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div style={{ background: C.navy, borderRadius: 20, padding: "3px 12px", fontFamily: FF.body, fontSize: 11, color: "#fff", fontWeight: 700, letterSpacing: "0.5px" }}>
                  {DATA.category.toUpperCase()}
                </div>
                <span style={{ fontFamily: FF.body, fontSize: 13, color: "#F5EBFA", fontWeight: 500 }}>{DATA.name}</span>
              </div>

              <h1
              style={{
                fontFamily: FF.body,
                fontWeight: 800,
                fontSize: "clamp(36px, 5.4vw, 58px)",
                lineHeight: 1.1,
                letterSpacing: "-1.2px",
                color: "#fff",
                margin: "0 0 18px",
              }}
            >
              Every panel,
              <br />
              wired for{" "}
              <span
                style={{
                  color: "#6C0A9F",
                  WebkitTextStroke: "0.6px rgba(255,255,255,0.85)",
                  textShadow: "0 1px 12px rgba(0,0,0,0.55)",
                }}
              >
                visibility.
              </span>
            </h1>
              <p style={{ fontFamily: FF.body, fontSize: "clamp(15px, 1.6vw, 17px)", color: "#EAE0F5", maxWidth: 500, margin: "0 0 36px", lineHeight: 1.75 }}>
                {DATA.description}
              </p>

              <div className="epm-cta-row" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
                <a
                  href={DEMO_MAILTO}
                  style={{
                    fontFamily: FF.body,
                    fontWeight: 700,
                    fontSize: 14.5,
                    padding: "14px 30px",
                    background: C.navy,
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 12,
                    border: `1.5px solid ${C.navy}`,
                    transition: "all 0.25s",
                    display: "inline-block",
                    boxShadow: "0 8px 24px rgba(108,10,159,0.45)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#52067A";
                    e.currentTarget.style.borderColor = "#52067A";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = C.navy;
                    e.currentTarget.style.borderColor = C.navy;
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  Request a Demo →
                </a>
                <a
                  href="/contact"
                  style={{
                    fontFamily: FF.body,
                    fontWeight: 600,
                    fontSize: 14.5,
                    padding: "14px 30px",
                    border: "1.5px solid rgba(255,255,255,0.45)",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 12,
                    transition: "all 0.25s",
                    display: "inline-block",
                    background: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                    e.currentTarget.style.borderColor = "#fff";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
                    e.currentTarget.style.transform = "none";
                  }}
                >
                  Talk to Us
                </a>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                {["QR-linked panel records", "Setup in days", "24/7 support"].map((t) => (
                  <div key={t} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.accentLight }} />
                    <span style={{ fontFamily: FF.body, fontSize: 13, color: "#E0D2F0", fontWeight: 500 }}>{t}</span>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 44,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 14,
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(216,180,254,0.3)",
                  backdropFilter: "blur(8px)",
                  borderRadius: 12,
                  padding: "14px 20px",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${C.navy}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: C.accentLight }}>⌁</div>
                <div>
                  <div style={{ fontFamily: FF.display, fontSize: 18, fontWeight: 700, color: "#fff" }}>607</div>
                  <div style={{ fontFamily: FF.body, fontSize: 11, color: "#D8B4FE", letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 600 }}>Panels Live</div>
                </div>
              </div>
            </div>
          </Section>

          <div style={{ padding: "0 40px", position: "relative", zIndex: 1 }}>
            <CircuitDivider />
          </div>
        </div>

        {/* ══ BENEFITS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section>
            <div style={{ marginBottom: 52, textAlign: "center" }}>
              <Label text="Key Benefits" />
              <SectionHeading>Built to keep every panel accounted for.</SectionHeading>
            </div>
            <div className="epm-grid-4">
              {DATA.benefits.map((b, i) => (
                <FeatureCard key={i} {...b} />
              ))}
            </div>
          </Section>
        </div>

        {/* ══ LIVE DASHBOARD SCREENSHOT + PRODUCT SCREENS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.surface }}>
          <Section>
            <div className="epm-grid-2" style={{ marginBottom: 72 }}>
              <div style={{ position: "relative" }}>
                <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: 16, overflow: "hidden", boxShadow: `0 24px 60px ${C.cardHoverGlow}` }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, background: C.navyD }}>
                    {["#6C0A9F", "#9333EA", "#C084FC"].map((c) => (
                      <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />
                    ))}
                    <span style={{ fontFamily: FF.mono, fontSize: 10.5, color: "#E9EDF3AA", marginLeft: 8, letterSpacing: "1px" }}>PANEL DASHBOARD</span>
                  </div>
                  <div style={{ background: C.raised }}>
                    <OptimizedImg
                      src={DATA.dashboardImage}
                      alt="AG Corporation panel dashboard"
                      loading="lazy"
                      pictureStyle={{ width: "100%", display: "block" }}
                      style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label text="Live Tracking" />
                <SectionHeading>Every panel, one dashboard, zero paperwork.</SectionHeading>
                <p style={{ fontSize: 15.5, color: C.subText, lineHeight: 1.8, margin: 0 }}>
                  Panels move from factory to site to commissioning across dozens of locations. This platform keeps every one of
                  them in a single live view, so your team always knows what's supplied, what's commissioned and what's next.
                </p>
              </div>
            </div>

            {/* ══ PRODUCT SCREENS GALLERY ══ */}
            <div>
              <div style={{ marginBottom: 40, textAlign: "center" }}>
                <Label text="Product Screens" />
                <SectionHeading>Panel tracking, screen by screen.</SectionHeading>
              </div>
              <div className="epm-grid-3">
                {DATA.screenshots.map((s, i) => (
                  <ScreenshotCard key={i} {...s} />
                ))}
              </div>
            </div>
          </Section>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section>
            <div className="epm-grid-2-start">
              <div>
                <Label text="How It Works" />
                <SectionHeading>
                  Five steps from factory
                  <br />
                  to warranty record.
                </SectionHeading>
                <p style={{ fontSize: 15, color: C.subText, lineHeight: 1.8, margin: "0 0 40px" }}>
                  Built around how a panel actually moves through AG Corporation — from registration through supply,
                  commissioning and long-term warranty tracking.
                </p>
                <a
                  href="/contact"
                  style={{
                    display: "inline-block",
                    fontFamily: FF.body,
                    fontWeight: 700,
                    fontSize: 14,
                    padding: "13px 26px",
                    border: `1.5px solid ${C.navy}`,
                    color: C.navy,
                    textDecoration: "none",
                    borderRadius: 10,
                    transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = C.navy;
                    e.currentTarget.style.color = "#fff";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 18px rgba(108,10,159,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = C.navy;
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  See it live →
                </a>
              </div>
              <div>
                {DATA.steps.map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 20, paddingBottom: i < DATA.steps.length - 1 ? 28 : 0, position: "relative" }}>
                    {i < DATA.steps.length - 1 && (
                      <div
                        style={{
                          position: "absolute",
                          left: 17,
                          top: 36,
                          bottom: 0,
                          width: 2,
                          background: `linear-gradient(to bottom, ${C.navy}55, transparent)`,
                        }}
                      />
                    )}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: C.surface,
                        border: `2px solid ${C.navy}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        zIndex: 1,
                        boxShadow: "0 2px 8px rgba(108,10,159,0.15)",
                      }}
                    >
                      <span style={{ fontFamily: FF.display, fontSize: 12, fontWeight: 700, color: C.navy }}>{s.n}</span>
                    </div>
                    <div style={{ paddingTop: 4 }}>
                      <h4 style={{ fontFamily: FF.display, fontSize: 17, fontWeight: 700, color: C.bright, margin: "0 0 5px" }}>{s.title}</h4>
                      <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.subText, margin: 0, lineHeight: 1.7 }}>{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        </div>

        {/* ══ FEATURES (consolidated) ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.surface }}>
          <Section style={{ width: "100%" }}>
            <div style={{ marginBottom: 40, textAlign: "center" }}>
              <Label text="Features" />
              <SectionHeading>
                {DATA.detailedFeatures.length} modules. One platform. No feature paywalls.
              </SectionHeading>
            </div>
            <div className="epm-grid-3" style={{ width: "100%" }}>
              {DATA.detailedFeatures.map((t, i) => (
                <FeatureTile key={i} title={t} />
              ))}
            </div>
          </Section>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.surface }}>
          <Section>
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <div style={{ marginBottom: 48, textAlign: "center" }}>
                <Label text="FAQ" />
                <SectionHeading>Common questions.</SectionHeading>
              </div>
              <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 16, overflow: "hidden", background: C.surface, boxShadow: "0 4px 16px rgba(108,10,159,0.04)" }}>
                {DATA.faq.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={i} style={{ borderBottom: i < DATA.faq.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <div
                        className="epm-faq-row"
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        style={{
                          padding: "20px 24px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 16,
                          background: open ? C.raised : "transparent",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!open) e.currentTarget.style.background = C.raised;
                        }}
                        onMouseLeave={(e) => {
                          if (!open) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 15.5, color: open ? C.navy : C.bright }}>{f.q}</span>
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            border: `1.5px solid ${open ? C.navy : C.muted}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: open ? C.navy : C.dimText,
                            fontSize: 14,
                            fontWeight: 700,
                            flexShrink: 0,
                            transition: "all 0.25s ease",
                            transform: open ? "rotate(45deg)" : "none",
                            background: open ? "#F3E8FF" : "transparent",
                          }}
                        >
                          +
                        </div>
                      </div>
                      {open && (
                        <div style={{ padding: "0 24px 22px", fontFamily: FF.body, fontSize: 14, color: C.subText, lineHeight: 1.8, borderTop: `1px solid ${C.border}` }}>
                          <div style={{ paddingTop: 16 }}>{f.a}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        </div>

        {/* ══ FINAL CTA ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, position: "relative", overflow: "hidden", background: C.navyD }}>
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at center, ${C.navy}66 0%, transparent 75%)` }} />
          <div style={{ position: "absolute", top: 20, left: 0, right: 0, opacity: 0.5 }}>
            <svg viewBox="0 0 1200 46" preserveAspectRatio="none" style={{ width: "200%", height: 46, animation: "epm-scroll 9s linear infinite" }}>
              <polyline
                fill="none"
                stroke={C.accentLight}
                strokeWidth="2"
                strokeLinecap="square"
                strokeLinejoin="miter"
                points="0,23 90,23 90,10 160,10 160,36 230,36 230,23 340,23 340,23 430,23 430,10 500,10 500,36 570,36 570,23 680,23 680,23 770,23 770,10 840,10 840,36 910,36 910,23 1020,23 1020,23 1110,23 1110,10 1180,10 1180,23 1200,23"
              />
            </svg>
          </div>
          <Section style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
            <Label text="Get Started" />
            <h2
              style={{
                fontFamily: FF.display,
                fontSize: "clamp(32px, 4.6vw, 54px)",
                fontWeight: 800,
                color: "#fff",
                margin: "0 0 20px",
                letterSpacing: "-1.2px",
                lineHeight: 1.15,
              }}
            >
              Ready to put every panel
              <br />
              <span style={{ color: C.accentLight }}>on the record?</span>
            </h2>
            <p style={{ fontFamily: FF.body, fontSize: 16, color: "#E0D2F0", maxWidth: 480, margin: "0 auto 44px", lineHeight: 1.7 }}>
              Join the sites already running {DATA.stats[0].value}+ panels through AG Corporation. Get a free walkthrough — no
              commitment.
            </p>
            <div className="epm-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <a
                href={DEMO_MAILTO}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 700,
                  fontSize: 15,
                  padding: "15px 34px",
                  background: C.navy,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 12,
                  border: `1.5px solid ${C.navy}`,
                  transition: "all 0.25s ease",
                  boxShadow: "0 10px 25px rgba(108,10,159,0.5)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#52067A";
                  e.currentTarget.style.borderColor = "#52067A";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = C.navy;
                  e.currentTarget.style.borderColor = C.navy;
                  e.currentTarget.style.transform = "none";
                }}
              >
                Book a Free Demo →
              </a>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 15,
                  padding: "15px 34px",
                  border: `1.5px solid rgba(255,255,255,0.45)`,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 12,
                  transition: "all 0.25s ease",
                  background: "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#fff";
                  e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.45)";
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "none";
                }}
              >
                📞 Call Us Now
              </a>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
};

export default AGCorporationProductPage;